import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, TextField, Textarea } from "@/shared/ui";
import { createPersona } from "@/shared/api/voice";
import { trackActionErrorMessage } from "@/shared/lib/trackActionErrors";
/**
 * Создание ИИ-голоса (персоны) из готового трака: один запрос createPersona.
 * Дальше голос собирается на бэке (processing) и появляется в менеджере
 * «Мой голос» — здесь не поллим, просто сообщаем об успехе.
 */
export const PersonaFromTrackModal = ({ opened, onClose, language, audioId, defaultName, onCreated, }) => {
    const ru = language === "ru";
    const [name, setName] = useState(defaultName ?? "");
    const [style, setStyle] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    // Пересеиваем поля при каждом открытии (другой трек).
    useEffect(() => {
        if (opened) {
            setName(defaultName ?? "");
            setStyle("");
            setError(null);
            setDone(false);
            setSubmitting(false);
        }
    }, [opened, defaultName]);
    const handleSubmit = async () => {
        if (audioId == null || name.trim().length === 0)
            return;
        setSubmitting(true);
        setError(null);
        try {
            await createPersona({
                audio_id: audioId,
                name: name.trim(),
                style: style.trim() || undefined,
            });
            setDone(true);
            onCreated?.();
        }
        catch (e) {
            const anyErr = e;
            setError(
            // Отказ «для этого трека недоступно» приходит кодом — показываем текст.
            trackActionErrorMessage(e, ru) ||
                anyErr?.response?.data?.detail ||
                (ru ? "Не удалось создать голос" : "Failed to create voice"));
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx(Modal, { opened: opened, onClose: onClose, size: "sm", title: ru ? "ИИ-голос из трека" : "AI voice from track", children: done ? (_jsxs("div", { children: [_jsx(Alert, { tone: "success", children: ru
                        ? "Голос создаётся. Он появится в разделе «Мой голос» через пару минут."
                        : "The voice is being created. It will appear in “My voice” in a couple of minutes." }), _jsx("div", { style: {
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "var(--vp-space-4)",
                    }, children: _jsx(Button, { variant: "primary", size: "md", onClick: onClose, children: ru ? "Готово" : "Done" }) })] })) : (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "var(--vp-space-3)" }, children: [_jsx("p", { style: { margin: 0, fontSize: "var(--vp-text-sm)", color: "var(--vp-text-muted)", lineHeight: 1.5 }, children: ru
                        ? "Создадим ИИ-голос на основе этого трека — потом сможете петь им новые песни."
                        : "We'll build an AI voice from this track — then you can sing new songs with it." }), _jsx(TextField, { label: ru ? "Название голоса" : "Voice name", value: name, maxLength: 60, onChange: (e) => setName(e.target.value), placeholder: ru ? "Например, «Голос из трека»" : "e.g. Voice from track" }), _jsx(Textarea, { label: ru ? "Стиль (необязательно)" : "Style (optional)", value: style, maxLength: 200, rows: 2, onChange: (e) => setStyle(e.target.value), placeholder: ru ? "Например, мягкий, поп" : "e.g. soft, pop" }), error && _jsx(Alert, { tone: "error", children: error }), _jsxs("div", { style: {
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "var(--vp-space-2)",
                        marginTop: "var(--vp-space-2)",
                    }, children: [_jsx(Button, { variant: "ghost", size: "md", onClick: onClose, children: ru ? "Отмена" : "Cancel" }), _jsx(Button, { variant: "primary", size: "md", loading: submitting, disabled: name.trim().length === 0 || audioId == null, onClick: () => void handleSubmit(), children: ru ? "Создать голос" : "Create voice" })] })] })) }));
};
